import { ArrowUpRight, X } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { services } from '@/content/et/services';
import { site } from '@/content/et/site';
import type { CalculatorMode, HourlyServiceId } from '@/content/types';
import { PricingCalculator } from '@/components/pricing/PricingCalculator';
import styles from './ServicesGrid.module.css';

function padIndex(n: number): string {
  return String(n).padStart(2, '0');
}

function parseMode(raw: string | null): CalculatorMode {
  if (raw === 'hourly' || raw === 'fixed' || raw === 'tire') return raw;
  return 'tire';
}

/** Map service id → fixed service row id when applicable */
function fixedIdForService(serviceId: string): string | undefined {
  if (serviceId === 'olivahetus') return 'olivahetus';
  if (serviceId === 'veakoodid') return 'veakoodid';
  return undefined;
}

/** Side frame is only shown from this breakpoint (see ServicesGrid.module.css). */
const DESKTOP_VISUAL_MQ = '(min-width: 900px)';

export function ServicesGrid() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeId, setActiveId] = useState(services[0]?.id ?? '');
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelMode, setPanelMode] = useState<CalculatorMode>('tire');
  const [panelHourly, setPanelHourly] = useState<HourlyServiceId | undefined>();
  const [panelFixed, setPanelFixed] = useState<string | undefined>();
  const [isDesktopVisual, setIsDesktopVisual] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia(DESKTOP_VISUAL_MQ).matches
      : false,
  );
  const sectionRef = useRef<HTMLElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const [light, setLight] = useState({ x: 50, y: 35, intensity: 0.045 });

  const active = services.find((s) => s.id === activeId) ?? services[0];

  const openForService = useCallback(
    (serviceId: string) => {
      const service = services.find((s) => s.id === serviceId);
      if (!service) return;
      setActiveId(serviceId);
      const mode = service.calculatorMode ?? 'tire';
      setPanelMode(mode);
      setPanelHourly(service.hourlyServiceId);
      setPanelFixed(fixedIdForService(serviceId));
      setPanelOpen(true);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('mode', mode);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const closePanel = useCallback(() => {
    setPanelOpen(false);
  }, []);

  // Deep-link: ?mode= / #hinnakiri opens panel
  useEffect(() => {
    const mode = searchParams.get('mode');
    const hash = window.location.hash;
    if (mode || hash === '#hinnakiri') {
      setPanelMode(parseMode(mode));
      setPanelOpen(true);
      if (hash === '#hinnakiri' || hash === '#teenused') {
        requestAnimationFrame(() => {
          document.getElementById('teenused')?.scrollIntoView({
            behavior: 'smooth',
          });
        });
      }
    }
  }, [searchParams]);

  // Panel open: scroll lock + hide mobile bottom nav
  useEffect(() => {
    const stickyMq = window.matchMedia('(max-width: 1023px)');
    const scrollMq = window.matchMedia('(max-width: 899px)');
    const prevOverflow = document.body.style.overflow;

    const syncPanelChrome = () => {
      if (!panelOpen) {
        document.body.style.overflow = prevOverflow;
        delete document.documentElement.dataset.calcPanelOpen;
        return;
      }
      document.body.style.overflow = scrollMq.matches ? 'hidden' : prevOverflow;
      if (stickyMq.matches) {
        document.documentElement.dataset.calcPanelOpen = '';
      } else {
        delete document.documentElement.dataset.calcPanelOpen;
      }
    };

    syncPanelChrome();
    if (panelOpen) closeBtnRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePanel();
    };
    window.addEventListener('keydown', onKey);
    stickyMq.addEventListener('change', syncPanelChrome);
    scrollMq.addEventListener('change', syncPanelChrome);

    return () => {
      document.body.style.overflow = prevOverflow;
      delete document.documentElement.dataset.calcPanelOpen;
      window.removeEventListener('keydown', onKey);
      stickyMq.removeEventListener('change', syncPanelChrome);
      scrollMq.removeEventListener('change', syncPanelChrome);
    };
  }, [panelOpen, closePanel]);

  // Ambient gradient
  useEffect(() => {
    let raf = 0;
    const update = () => {
      const el = sectionRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const progress =
        1 - (rect.top + rect.height * 0.5) / (vh + rect.height * 0.5);
      const p = Math.min(1, Math.max(0, progress));
      setLight({
        x: 42 + p * 18,
        y: 18 + p * 58,
        intensity: 0.035 + Math.sin(p * Math.PI) * 0.028,
      });
    };
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  // Desktop gallery: mount + preload all service images so hover is instant
  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_VISUAL_MQ);
    const sync = () => setIsDesktopVisual(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (!isDesktopVisual) return;
    const warmed = new Set<string>();
    for (const service of services) {
      const src = service.image;
      if (!src || warmed.has(src)) continue;
      warmed.add(src);
      const img = new Image();
      img.decoding = 'async';
      img.src = src;
    }
  }, [isDesktopVisual]);

  return (
    <section
      id="teenused"
      ref={sectionRef}
      className={`section ${styles.section}`}
      aria-labelledby="teenused-title"
    >
      {/* Anchor for legacy #hinnakiri links */}
      <div id="hinnakiri" className={styles.hashAnchor} aria-hidden />

      <div className="container">
        <header className={styles.header}>
          <div>
            <h2
              id="teenused-title"
              className="section__title section__title--tight"
            >
              {site.nav.services}
            </h2>
            <p className={styles.sectionLead}>
              Valige teenus — kalkulaator avaneb küljelt.
            </p>
          </div>
        </header>

        <div
          className={[styles.layout, panelOpen ? styles.layoutPanelOpen : '']
            .filter(Boolean)
            .join(' ')}
        >
          <div className={styles.indexShell}>
            <div
              className={styles.ambient}
              aria-hidden="true"
              style={
                {
                  '--light-x': `${light.x}%`,
                  '--light-y': `${light.y}%`,
                  '--light-a': String(light.intensity),
                } as CSSProperties
              }
            />
            <ol className={styles.index} role="list">
              {services.map((service, i) => {
                const isActive = service.id === active?.id;
                const isSelected = panelOpen && service.id === activeId;
                return (
                  <li key={service.id}>
                    <button
                      type="button"
                      className={[
                        styles.row,
                        isActive ? styles.rowActive : '',
                        isSelected ? styles.rowSelected : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onMouseEnter={() => setActiveId(service.id)}
                      onFocus={() => setActiveId(service.id)}
                      onClick={() => openForService(service.id)}
                      aria-controls={panelId}
                      aria-expanded={panelOpen && isSelected}
                    >
                      <span className={styles.num} aria-hidden="true">
                        {padIndex(i + 1)}
                      </span>
                      <span className={styles.rowMain}>
                        <span className={styles.rowTitle}>{service.title}</span>
                        <span className={styles.rowDesc}>
                          {service.description}
                        </span>
                      </span>
                      <span className={styles.rowAction} aria-hidden="true">
                        <ArrowUpRight size={20} strokeWidth={1.75} />
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </div>

          {/* Desktop preview image — hides when calculator panel open */}
          <aside
            className={[
              styles.visual,
              panelOpen ? styles.visualHidden : '',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-hidden="true"
          >
            <div className={styles.frame}>
              <span className={styles.corner} data-c="tl" />
              <span className={styles.corner} data-c="tr" />
              <span className={styles.corner} data-c="bl" />
              <span className={styles.corner} data-c="br" />
              {/* Stack all slides on desktop: preloaded layers crossfade, no src swap flash */}
              {(isDesktopVisual
                ? services
                : services.filter((s) => s.id === active?.id)
              ).map((service, i) => {
                const src = service.image ?? '/images/engine.webp';
                const isActive = service.id === active?.id;
                return (
                  <img
                    key={service.id}
                    src={src}
                    alt=""
                    className={[
                      styles.visualImg,
                      isActive ? styles.visualImgActive : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    style={
                      service.imagePosition
                        ? { objectPosition: service.imagePosition }
                        : undefined
                    }
                    width={960}
                    height={536}
                    loading="eager"
                    decoding="async"
                    fetchPriority={i === 0 && isActive ? 'high' : 'low'}
                    draggable={false}
                  />
                );
              })}
              <div className={styles.visualMeta}>
                <span className={styles.visualIndex}>
                  {padIndex(
                    services.findIndex((s) => s.id === active?.id) + 1 || 1,
                  )}
                </span>
                <span className={styles.visualLabel}>{active?.title}</span>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Backdrop (mobile primarily) */}
      <button
        type="button"
        className={[styles.backdrop, panelOpen ? styles.backdropOpen : '']
          .filter(Boolean)
          .join(' ')}
        aria-label="Sulge kalkulaator"
        tabIndex={panelOpen ? 0 : -1}
        onClick={closePanel}
      />

      {/* Calculator slides in from the right */}
      <div
        id={panelId}
        className={[styles.calcPanel, panelOpen ? styles.calcPanelOpen : '']
          .filter(Boolean)
          .join(' ')}
        role="dialog"
        aria-modal="true"
        aria-label={site.calculator.title}
        aria-hidden={!panelOpen}
      >
        <div className={styles.calcPanelInner}>
          {panelOpen ? (
            <PricingCalculator
              panel
              onClose={closePanel}
              externalMode={panelMode}
              externalHourlyServiceId={panelHourly}
              externalFixedServiceId={panelFixed}
            />
          ) : null}
        </div>
        {/* Desktop close always in corner if panel renders without calc close focus */}
        <button
          ref={closeBtnRef}
          type="button"
          className={styles.calcPanelCloseSr}
          onClick={closePanel}
          tabIndex={-1}
          aria-hidden
        >
          <X size={18} />
        </button>
      </div>
    </section>
  );
}
